import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import HeadData from 'components/Common/HeadData'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isBedbankOffer, isHotel } from 'lib/offer/offerTypes'
import config from 'constants/config'
import {
  getCheckInDateFromURLSearchParams,
  getCheckOutDateFromURLSearchParams,
  getFlexibleMonthsFromURLSearchParams,
  getFlexibleNightsFromURLSearchParams,
} from 'lib/url/searchUrlUtils'
import useQueryParams from 'hooks/useQueryParams'
import { getPageTitle } from 'components/Search/utils'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { getHeadDescription } from 'components/OfferPage/utils'
import type { SearchFilterParams } from 'lib/search/searchUtils'
import MetaSearchStructuredData from 'components/Pages/HotelSearchPage/MetaSearchStructuredData'

interface Props extends RouteComponentProps {
  offer: App.BedbankOffer
  searchParams: SearchFilterParams
  filters: App.OfferListFilters
}

function MetaSearchPageHeadData(props: Props) {
  const { offer, searchParams, filters } = props
  const intl = useIntl()
  const queryParams = useQueryParams()

  const pageTitle = useMemo(() => {
    const { searchItem } = searchParams
    const checkInDate = getCheckInDateFromURLSearchParams(queryParams)
    const checkOutDate = getCheckOutDateFromURLSearchParams(queryParams)
    const flexibleMonths = getFlexibleMonthsFromURLSearchParams(queryParams)
    const flexibleNights = getFlexibleNightsFromURLSearchParams(queryParams)
    const propertyName = isHotel(offer) ? offer.property?.name : 'Luxury Hotel'

    return getPageTitle(intl, {
      ...searchItem,
      format: { mainText: propertyName },
      searchType: 'property',
      value: '',
    }, { checkIn: checkInDate, checkOut: checkOutDate, flexibleMonths, flexibleNights: flexibleNights as FLEXIBLE_DURATION_RANGE })
  }, [intl, queryParams, searchParams, offer])

  const pageDescription = useMemo(() => {
    if (!offer || !isHotel(offer)) {
      return config.head.defaultDescription
    }
    if (isBedbankOffer(offer)) {
      return `Book the best deal for ${offer.name}, ${offer.property.address.city} with Luxury Escapes today. The world's best escapes, handpicked by experts!`
    }
    return getHeadDescription(offer, true)
  }, [offer])

  return (
    <>
      <HeadData title={pageTitle} description={pageDescription} />
      {offer.property && <MetaSearchStructuredData offer={offer} filters={filters} />}
    </>
  )
}
export default withRouter(MetaSearchPageHeadData)
